import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './AuthForm.css';
import { notify } from '../utils/notify';
import { API_BASE } from '../config/api';

function AuthForm({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [ruleHint, setRuleHint] = useState('');
  const googleButtonRef = useRef(null);
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  const phoneRegex = /^\+?\d{8,15}$/;
  const passwordRegex = /^.{4,128}$/;

  const handleGoogleCredential = useCallback(async (credentialResponse) => {
    try {
      const credential = String(credentialResponse?.credential || '').trim();
      if (!credential) {
        notify('Connexion Google impossible.');
        return;
      }
      const res = await axios.post(`${API_BASE}/auth/google`, { credential });
      onLogin(res.data.user);
      notify('Connexion Google reussie.');
    } catch (err) {
      notify(err.response?.data?.error || err.message);
    }
  }, [onLogin]);

  useEffect(() => {
    if (!isLogin || !GOOGLE_CLIENT_ID || !googleButtonRef.current) return undefined;

    const initGoogleButton = () => {
      if (!window.google?.accounts?.id || !googleButtonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential
      });
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        text: 'continue_with',
        size: 'large',
        shape: 'pill',
        width: 320
      });
    };

    if (window.google?.accounts?.id) {
      initGoogleButton();
      return undefined;
    }

    let script = document.querySelector('script[data-google-identity="booktube"]');
    const onLoad = () => initGoogleButton();
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.setAttribute('data-google-identity', 'booktube');
      document.body.appendChild(script);
    }
    script.addEventListener('load', onLoad);
    return () => script.removeEventListener('load', onLoad);
  }, [GOOGLE_CLIENT_ID, handleGoogleCredential, isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRuleHint('');

    const normalizedIdentifier = String(identifier || '').trim();
    const normalizedEmail = normalizedIdentifier.toLowerCase();
    const normalizedPhone = normalizedIdentifier.replace(/\s+/g, '');
    const isEmail = emailRegex.test(normalizedEmail);
    const isPhone = phoneRegex.test(normalizedPhone);

    if (!isEmail && !isPhone) {
      setRuleHint('Entrez un email valide ou un numero de telephone valide.');
      return;
    }

    if (!passwordRegex.test(String(password || ''))) {
      setRuleHint('Mot de passe: 4 a 128 caracteres.');
      return;
    }

    try {
      if (isLogin) {
        const res = await axios.post(`${API_BASE}/login`, {
          identifier: isEmail ? normalizedEmail : normalizedPhone,
          password
        });
        onLogin(res.data.user);
      } else {
        const formData = new FormData();
        formData.append('full_name', fullName);
        formData.append('identifier', isEmail ? normalizedEmail : normalizedPhone);
        formData.append('password', password);
        if (profilePicture) formData.append('profilePicture', profilePicture);

        await axios.post(`${API_BASE}/register`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const loginRes = await axios.post(`${API_BASE}/login`, {
          identifier: isEmail ? normalizedEmail : normalizedPhone,
          password
        });
        onLogin(loginRes.data.user);
        notify('Compte cree et connexion reussie.');
      }

      setIdentifier('');
      setPassword('');
      setFullName('');
      setProfilePicture(null);
      setRuleHint('');
    } catch (err) {
      notify(err.response?.data?.error || err.message);
    }
  };

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <div className="auth-tabs">
        <button type="button" className={isLogin ? 'active' : ''} onClick={() => setIsLogin(true)}>
          Connexion
        </button>
        <button type="button" className={!isLogin ? 'active' : ''} onClick={() => setIsLogin(false)}>
          Creer un compte
        </button>
      </div>

      {!isLogin && (
        <>
          <input
            type="text"
            placeholder="Nom complet"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />

          <div className="profile-upload">
            <label>Photo de profil :</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setProfilePicture(e.target.files[0])}
            />
            {profilePicture && (
              <img
                src={URL.createObjectURL(profilePicture)}
                alt="Previsualisation"
                style={{ width: 80, height: 80, borderRadius: '50%', marginTop: 5 }}
              />
            )}
          </div>
        </>
      )}

      <input
        type="text"
        placeholder="Email ou numero de telephone"
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        maxLength={120}
        required
      />
      <div className="password-field">
        <input
          type={showPassword ? 'text' : 'password'}
          placeholder="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={4}
          maxLength={128}
          required
        />
        <button
          type="button"
          className="password-toggle-btn"
          onClick={() => setShowPassword((prev) => !prev)}
          aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          title={showPassword ? 'Masquer' : 'Afficher'}
        >
          {showPassword ? '🙈' : '👁'}
        </button>
      </div>

      <small className="auth-rule">
        Mot de passe: 4 a 128 caracteres, libre (sans obligation de chiffre ou signe).
      </small>
      {ruleHint && <small className="auth-rule auth-rule-error">{ruleHint}</small>}

      <button type="submit">{isLogin ? 'Se connecter' : 'Creer un compte'}</button>
      {isLogin && GOOGLE_CLIENT_ID && (
        <div className="google-login-block">
          <small>Ou utilisez votre compte Google</small>
          <div ref={googleButtonRef} className="google-login-btn" />
        </div>
      )}
    </form>
  );
}

export default AuthForm;


